import type { PricingAlertsTreeAlert } from './PricingAlerts'

import { any, map, prop, uniq } from 'lodash/fp'
import { ChevronRightIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { getAlertLevelStyling } from '@/entities/price-alert'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { useDisclosure } from '@/shared/hooks/use-disclosure'
import { ampli } from '@/shared/lib/ampli'
import { cn } from '@/shared/lib/utils'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import ListGroup, {
	ListGroupContent,
	ListGroupEnd,
	ListGroupInner,
	ListGroupItem,
	ListGroupStart,
} from '../../../shared/components/ListGroup'
import { PricingAlertsSolutionPopup } from './PricingAlertsSolutionPopup'

type PricingAlertsGroupProps = {
	alertsOf: 'pc' | 'whatif' | 'og' | 'assortment' | 'anchoring'
	count: number
	kind: string
	percent: number
	alerts: PricingAlertsTreeAlert[]
	onFilterClick?: (ids: string[]) => void
	collapsedByDefault?: boolean
	optimizationGroupId?: string
}
export default function PricingAlertsGroup({
	alertsOf,
	kind,
	count,
	alerts,
	percent,
	onFilterClick,
	collapsedByDefault = false,
}: PricingAlertsGroupProps) {
	const { t } = useTranslation()

	const { isOpen, toggle } = useDisclosure(collapsedByDefault)
	const { backgroundClassNames, badgeVariant } = getAlertLevelStyling(kind)
	const handleCarretClick = () => {
		ampli.alertsSectionToggled({
			alert_kind: kind,
			alerts_of: alertsOf,
		})

		toggle()
	}
	const handleShowAllButtonClick = () => {
		ampli.alertsItemBadgeClicked({
			alert_kind: kind,
			alerts_of: alertsOf,
			is_alert_exact: false,
		})
		onFilterClick && onFilterClick(uniq(map(prop('id'), alerts)))
	}
	const handleShowButtonClick = (id: string) => {
		ampli.alertsItemBadgeClicked({
			alert_kind: kind,
			alerts_of: alertsOf,
			is_alert_exact: true,
		})
		onFilterClick && onFilterClick([id])
	}

	return (
		<ListGroup
			className={cn('rounded-lg border fade-in', backgroundClassNames)}
		>
			<ListGroupItem
				className={cn('relative cursor-text rounded-t-lg p-2', {
					'rounded-b-lg': !isOpen,
				})}
			>
				<ListGroupInner className={cn(any(prop('solution'), alerts) && 'pr-9')}>
					<ListGroupStart
						className='mr-2 cursor-pointer'
						onClick={handleCarretClick}
					>
						<Button variant='ghost' size='icon-sm'>
							<ChevronRightIcon
								style={{
									transform: `rotate(${isOpen ? 90 : 0}deg)`,
									transition: 'transform 150ms',
								}}
								className='flex size-4 items-center text-muted-foreground dark:text-white'
							/>
						</Button>
					</ListGroupStart>
					<ListGroupContent className='text-sm font-semibold'>
						{t(kind, { defaultValue: kind })}
					</ListGroupContent>
					<ListGroupEnd>
						<Badge
							data-testid={`badge_${kind}`}
							variant={badgeVariant}
							{...(onFilterClick && {
								onClick: handleShowAllButtonClick,
								interactive: true,
								className: 'cursor-pointer',
							})}
						>{`${formatNumber(count)} (${formatNumber(
							percent,
							NumberFormats.Percent
						)})`}</Badge>
					</ListGroupEnd>
				</ListGroupInner>
			</ListGroupItem>
			{isOpen &&
				alerts?.map(({ name, products, id, est_revenue_share, solution }) => {
					const formattedMetrics = {
						products: formatNumber(products),
						revenue: formatNumber(est_revenue_share, NumberFormats.Percent),
					}

					return (
						<ListGroupItem
							key={id}
							className={cn(
								'list-group-rounded relative cursor-text py-2 pl-10',
								solution ? 'ps-2 pe-2' : 'pr-2'
							)}
							data-testid={`alert_${id}`}
						>
							<ListGroupInner>
								<ListGroupContent className='text-sm'>
									{t(name, { defaultValue: name })}
								</ListGroupContent>
								<ListGroupEnd>
									<Badge
										data-testid={`badge_${kind}_${id}`}
										variant={badgeVariant}
										{...(onFilterClick && {
											onClick: () => handleShowButtonClick(id),
											interactive: true,
											className: 'cursor-pointer',
										})}
									>
										{formattedMetrics.products} ({formattedMetrics.revenue})
									</Badge>
									{solution && (
										<PricingAlertsSolutionPopup
											kind={kind}
											alertsOf={alertsOf}
											badgeContent={{ formattedMetrics, badgeVariant }}
											name={name}
											solution={solution}
										/>
									)}
								</ListGroupEnd>
							</ListGroupInner>
						</ListGroupItem>
					)
				})}
		</ListGroup>
	)
}
